import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
	getFilteredRowModel,
	createColumnHelper,
	getPaginationRowModel,
	getFacetedUniqueValues,
	type SortingState,
	type PaginationState,
	type RowSelectionState,
	type Updater,
	type VisibilityState,
	type ColumnFiltersState,
	type ColumnFilter,
	type ColumnDef,
	type FilterFnOption,
} from '@tanstack/react-table';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Checkbox } from '@chakra-ui/react';
import { createDataCollectionTable } from './DataCollectionTable';
import { type DataCollectionContextProps, type DataCollectionProviderProps } from './DataCollection.types';
import { useNavigate, useSearchParams } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';
import { dbTimeToJsDate } from '../utils/dateUtils';
import { SmsMessageRecipientDeliveryStatusGroups } from '../utils/deliveryStatuses';
import { toaster } from '../components/ui/toaster';
import { parseErrorToString } from '../utils/parseErrorToString';

const getFilterOperator = <T,>(filterFn: FilterFnOption<T>) => {
	if (filterFn === 'arrIncludesSomeSensitive' || filterFn === 'arrIncludesSome') return 'in';
	if (filterFn === 'dateTimeGreaterThan') return 'gt';
	if (filterFn === 'dateTimeLessThan') return 'lt';
	return 'in';
};

const parseColumnFiltersToApiFilters = <T,>(columnFilters: Array<ColumnFilter & { operator?: string }>, columns: Array<ColumnDef<T, string>>) => {
	const mappedFilters = columnFilters.map(cf => {
		const id = cf.id;

		const column = columns.find(c => {
			return 'accessorKey' in c ? c.accessorKey === id || c.id === id : c.id === id;
		});

		if (!column?.filterFn) return null;
		const rawValue = cf.value;

		if (column.filterFn === 'dateTimeBetween') {
			const isArr = Array.isArray(rawValue);
			const firstDateValue = isArr && typeof rawValue[0] === 'string' && !!rawValue[0] ? `${id}:gt:${rawValue[0]}` : '';
			const secondDateValue = isArr && typeof rawValue[1] === 'string' && !!rawValue[1] ? `${id}:lt:${rawValue[1]}` : '';
			const isBoth = !!firstDateValue && !!secondDateValue ? '|' : '';
			if (!firstDateValue && !secondDateValue) return null;
			return { filter: `${firstDateValue}${isBoth}${secondDateValue}` };
		}

		const operator = getFilterOperator<T>(column.filterFn);
		let value: string | number = '';
		const valueIsArray = Array.isArray(rawValue);
		const isRelatedFilter = id.split('.')?.[1];

		if (valueIsArray) {
			value = rawValue.join(',');
		} else {
			value = rawValue as string | number;
		}

		return { filter: `${isRelatedFilter ? id.replace('.', ':') : id}:${operator}:${value}`, isRelatedFilter };
	});

	if (!mappedFilters || mappedFilters.length === 0) return null;

	const filters = mappedFilters
		.filter(f => !f?.isRelatedFilter)
		.map(fi => fi?.filter)
		.join('|');
	const relatedFilters = mappedFilters
		.filter(f => f?.isRelatedFilter)
		.map(fi => fi?.filter)
		.join('|');

	return {
		...(filters && { filters }),
		...(relatedFilters && { relatedFilters }),
	};
};

export function createDataCollectionContext<T extends Record<string, unknown>>() {
	'use no memo';
	const DataCollectionContext = createContext<DataCollectionContextProps<T> | null>(null);
	const emptyArray: Array<T> = [];

	const DataCollectionContextProvider = ({
		fetchFn,
		queryParams,
		dataKey,
		keyAttribute,
		columns,
		children,
		initialItemsPerPage,
		isServerSide = true,
		isSelectable = true,
		defaultHiddenColumns,
		selectedItemsActions,
		defaultSort,
		useParamsToSort = true,
		emptyDataText,
		emptyDataIcon,
		onClickRow,
		isLoading = false,
		customQueryKey,
		selectAllFn,
		refetchInterval,
	}: DataCollectionProviderProps<T>) => {
		const columnHelper = createColumnHelper<T>();
		const [searchParams, setSearchParams] = useSearchParams();
		const parsedApiGetRelated = queryParams?.getRelated ? { getRelated: queryParams?.getRelated } : null;
		const navigate = useNavigate();
		const [pagination, setPagination] = useState<PaginationState>({
			pageIndex: 0,
			pageSize: initialItemsPerPage,
		});
		const parsedApiPagination = {
			page: (pagination.pageIndex + 1).toString(),
			itemsPerPage: pagination.pageSize.toString(),
		};

		const defaultSorting = useMemo(() => {
			const sortParam = useParamsToSort ? searchParams.get('sort') : null;
			const relatedSortParam = useParamsToSort ? searchParams.get('relatedSort') : null;

			const activeSort = relatedSortParam ?? sortParam;
			if (!activeSort && defaultSort) return [defaultSort];
			if (!activeSort) return [];

			const isRelated = !!relatedSortParam;

			if (isRelated) {
				return [
					{
						id: activeSort,
						desc: activeSort.includes(':desc'),
					},
				];
			}

			// Normal sort: "lastName:asc"
			const [id, direction] = activeSort.split(':');
			return [{ id, desc: direction === 'desc' }];
		}, [searchParams, defaultSort]);
		const defaultFiltering = useMemo(() => {
			try {
				const filterParams = searchParams.get('filter')?.split('|');
				if (!filterParams) return [];

				return filterParams.map(fp => {
					const [id, value] = fp.split(':');
					if (!id || !value) {
						throw new Error('DataCollectionContext: Default filtering id or value missing. Probably malformed queryParams.');
					}

					if (id === 'deliveryStatusCode') {
						// Split the codes, convert to numbers
						const codes = value.split(',').map(v => Number(v));

						// Map codes to their categories with their actual codes as strings
						const categoryMap = new Map<keyof typeof SmsMessageRecipientDeliveryStatusGroups, number[]>();
						codes.forEach(code => {
							for (const [group, groupCodes] of Object.entries(SmsMessageRecipientDeliveryStatusGroups)) {
								if (groupCodes.includes(code)) {
									const groupKey = group as keyof typeof SmsMessageRecipientDeliveryStatusGroups;
									if (!categoryMap.has(groupKey)) categoryMap.set(groupKey, []);
									categoryMap.get(groupKey)!.push(code);
									break;
								}
							}
						});

						if (categoryMap.size === 0) {
							throw new Error(`Unknown deliveryStatusCode(s): ${value}`);
						}

						const resultValues = Array.from(categoryMap.values()).map(codes => codes.join(','));

						return {
							id,
							value: resultValues,
						};
					}

					return { id, value: value?.split(',') };
				});
			} catch (error: unknown) {
				const cleanedUrl = window.location.pathname;
				navigate(cleanedUrl);
				console.error(parseErrorToString(error));
				toaster.create({
					type: 'error',
					title: 'Something went wrong with filtering content. Try again later.',
				});
				return [];
			}
		}, [searchParams]);

		const [savedFilters, setSavedFilters] = useLocalStorage<Record<string, ColumnFiltersState>>('savedFilters', {});

		const [savedSorting, setSavedSorting] = useLocalStorage<Record<string, SortingState>>('savedSorting', {});
		const parsedSavedSorting = savedSorting[dataKey + customQueryKey || ''] ?? [];

		const defaultSearch = searchParams.get('search') ?? '';

		// @ts-expect-error undef id is fine.
		const [sorting, setSorting] = useState<SortingState>(parsedSavedSorting.length > 0 ? parsedSavedSorting : defaultSorting);
		const [search, setSearch] = useState(defaultSearch);
		const parsedSavedFilters = savedFilters[dataKey + customQueryKey || ''] ?? [];

		const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(parsedSavedFilters.length > 0 ? parsedSavedFilters : defaultFiltering);
		const handleSort = (updater: Updater<SortingState>) => {
			const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
			const sort = newSorting[0];

			const params = new URLSearchParams(searchParams);

			if (sort) {
				if (sort.id.includes(':') && !sort.id.startsWith('sort:')) {
					// Treat as related sort (full string)
					params.set('relatedSort', sort.id.replace(/:(asc|desc):/g, sort.desc ? ':desc:' : ':asc:'));
					params.delete('sort');
				} else {
					// Normal sort
					params.set('sort', `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
					params.delete('relatedSort');
				}
			} else {
				params.delete('sort');
				params.delete('relatedSort');
			}

			setSearchParams(params);
			setSorting(sort ? [sort] : newSorting);
			setSavedSorting({
				...savedSorting,
				[dataKey + customQueryKey || '']: sort ? [sort] : newSorting,
			});
			setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
		};
		const handleSearch = (updater: Updater<string>) => {
			const newFilter = typeof updater === 'function' ? updater(search) : updater;

			const params = new URLSearchParams(searchParams);
			if (newFilter) {
				params.set('search', newFilter);
			} else {
				params.delete('search');
			}
			setSearchParams(params);
			setSearch(newFilter);
			setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
		};

		const handleColumnFilter = (updater: Updater<ColumnFiltersState>) => {
			const newColumnFilters = typeof updater === 'function' ? updater(columnFilters) : updater;

			const params = new URLSearchParams(searchParams);

			if (newColumnFilters.length > 0) {
				params.set(
					'filter',
					newColumnFilters
						.map(cf => {
							if (Array.isArray(cf.value)) {
								return `${cf.id}:${cf.value.join(',')}`;
							}
							return `${cf.id}:${cf.value}`;
						})
						.join('|'),
				);
			} else {
				params.delete('filter');
			}

			setSearchParams(params);
			setColumnFilters(newColumnFilters);

			/**
			 * Only save filters on superadmin.
			 */
			if (window.location.origin !== import.meta.env.VITE_APP_URL) {
				setSavedFilters({
					...savedFilters,
					[dataKey + customQueryKey || '']: newColumnFilters,
				});
			}

			setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
		};
		const parsedApiSorting = useMemo(() => {
			if (!sorting[0]) return null;

			const { id, desc } = sorting[0];

			// If it’s a related sort, send the full string to API
			if (id.includes(':') && !id.startsWith('sort:')) {
				return { relatedSort: id.replace(/:(asc|desc):/g, desc ? ':desc:' : ':asc:') };
			}

			return { sort: `${id}:${desc ? 'desc' : 'asc'}` };
		}, [sorting]);
		const parsedApiSearch = { search: search };
		const columnApiFilters = useMemo(() => {
			return parseColumnFiltersToApiFilters<T>(columnFilters, columns);
		}, [columnFilters, columns]);

		const parsedApiFilters = useMemo(() => {
			const mergedFilters = [queryParams?.filters, columnApiFilters?.filters].filter(Boolean).join('|');
			const mergedRelatedFilters = [queryParams?.relatedFilters, columnApiFilters?.relatedFilters].filter(Boolean).join('|');

			if (!mergedFilters && !mergedRelatedFilters) return null;
			return {
				...(mergedFilters && { filters: mergedFilters }),
				...(mergedRelatedFilters && { relatedFilters: mergedRelatedFilters }),
			};
		}, [queryParams?.filters, queryParams?.relatedFilters, columnApiFilters]);

		const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

		const [savedVisibleColumns, setSavedVisibleColumns] = useLocalStorage<Record<string, VisibilityState>>('visibleColumns', {});
		const parsedSavedVisibleColumns = savedVisibleColumns[dataKey + customQueryKey || ''];

		const handleChangeVisibleColumns = (updater: Updater<VisibilityState>) => {
			const prev = savedVisibleColumns[dataKey + customQueryKey || ''] ?? {};
			const next = typeof updater === 'function' ? updater(prev) : updater;

			setSavedVisibleColumns({
				...savedVisibleColumns,
				[dataKey + customQueryKey || '']: next,
			});
		};

		// if no entry for visible columns, set the default hidden columns to local storage
		useEffect(() => {
			if (!savedVisibleColumns[dataKey + customQueryKey || ''] && defaultHiddenColumns?.length) {
				const hiddenState = defaultHiddenColumns.reduce<VisibilityState>((acc, col) => {
					acc[col] = false;
					return acc;
				}, {});

				setSavedVisibleColumns({
					...savedVisibleColumns,
					[dataKey + customQueryKey || '']: hiddenState,
				});
			}
		}, [dataKey, defaultHiddenColumns, savedVisibleColumns, setSavedVisibleColumns]);

		const queryKey = isServerSide
			? [
					customQueryKey ? customQueryKey : dataKey,
					parsedApiPagination,
					parsedApiSorting?.relatedSort ?? parsedApiSorting?.sort,
					queryParams,
					parsedApiSearch,
					parsedApiFilters,
				]
			: [dataKey];

		const dataQuery = useQuery({
			queryKey: queryKey,
			queryFn: () => {
				return isServerSide
					? fetchFn({
							...queryParams,
							...(parsedApiFilters ? { ...parsedApiFilters } : {}),
							...parsedApiPagination,
							...parsedApiSorting,
							...parsedApiSearch,
						})
					: fetchFn({ ...queryParams, ...(parsedApiGetRelated ? { ...parsedApiGetRelated } : {}) });
			},
			placeholderData: keepPreviousData,
			refetchInterval: query => refetchInterval?.(query.state.data) ?? false,
		});

		const paginationServerState = isServerSide ? dataQuery?.data?.pagination : undefined;

		const checkboxColumn = columnHelper.display({
			id: 'checkbox-header',
			enableSorting: false,
			header: ({ table }) => (
				<Checkbox.Root checked={table.getIsAllRowsSelected()} onChange={table.getToggleAllRowsSelectedHandler()}>
					<Checkbox.HiddenInput />
					<Checkbox.Control>
						<Checkbox.Indicator />
					</Checkbox.Control>
				</Checkbox.Root>
			),
			cell: ({ row }) => {
				return (
					<Checkbox.Root checked={row.getIsSelected()} disabled={!row.getCanSelect()} onChange={row.getToggleSelectedHandler()}>
						<Checkbox.HiddenInput />
						<Checkbox.Control>
							<Checkbox.Indicator />
						</Checkbox.Control>
					</Checkbox.Root>
				);
			},
		});

		const columnsToUse = isSelectable ? [checkboxColumn, ...columns] : columns;

		// eslint-disable-next-line
		const table = useReactTable<T>({
			data: dataQuery?.data?.data[dataKey] ?? emptyArray,
			columns: columnsToUse,
			defaultColumn: {
				sortingFn: (rowA, rowB, columnId) => {
					const a = rowA.getValue(columnId);
					const b = rowB.getValue(columnId);

					const aNull = a == null;
					const bNull = b == null;

					if (aNull && bNull) return 0;
					if (aNull) return 1; // nulls last
					if (bNull) return -1;

					if (typeof a === 'number' && typeof b === 'number') {
						return a - b;
					}

					return String(a).localeCompare(String(b));
				},
			},
			state: {
				sorting,
				pagination,
				rowSelection,
				globalFilter: search,
				columnVisibility: parsedSavedVisibleColumns as VisibilityState,
				columnFilters,
			},
			getCoreRowModel: getCoreRowModel(),
			getSortedRowModel: getSortedRowModel(),
			getPaginationRowModel: getPaginationRowModel(),
			getFilteredRowModel: getFilteredRowModel(),
			onColumnFiltersChange: handleColumnFilter,
			onColumnVisibilityChange: handleChangeVisibleColumns,
			onRowSelectionChange: setRowSelection,
			enableRowSelection: isSelectable,
			onPaginationChange: setPagination,
			onSortingChange: handleSort,
			onGlobalFilterChange: handleSearch,
			manualPagination: isServerSide,
			manualSorting: isServerSide,
			manualFiltering: isServerSide,
			getFacetedUniqueValues: getFacetedUniqueValues(),
			getRowId: row => {
				if (typeof row[keyAttribute] === 'string') return row[keyAttribute];
				if (typeof row[keyAttribute] === 'number') return row[keyAttribute].toString();
				return '';
			},
			filterFns: {
				arrIncludesSomeSensitive: (row, columnId, filterValue) => {
					const cellValue = row.getValue(columnId);
					return filterValue.includes(cellValue);
				},
				dateTimeGreaterThan: (row, columnId, filterValue) => {
					const cellValue = row.getValue(columnId);
					if (typeof cellValue !== 'string') return false;
					const cellValueToJsDate = dbTimeToJsDate(cellValue);
					return !filterValue[0] || cellValueToJsDate > dbTimeToJsDate(filterValue[0]);
				},
				dateTimeLessThan: (row, columnId, filterValue) => {
					const cellValue = row.getValue(columnId);
					if (typeof cellValue !== 'string') return false;
					const cellValueToJsDate = dbTimeToJsDate(cellValue);
					return !filterValue[0] || cellValueToJsDate < dbTimeToJsDate(filterValue[0]);
				},
				dateTimeBetween: (row, columnId, filterValue) => {
					const cellValue = row.getValue(columnId);
					if (typeof cellValue !== 'string') return false;
					const cellValueToJsDate = dbTimeToJsDate(cellValue);
					const greaterThanFilterValue = filterValue[0] ? dbTimeToJsDate(filterValue[0]) : null;
					const lessThanFilterValue = filterValue[1] ? dbTimeToJsDate(filterValue[1]) : null;

					return (!greaterThanFilterValue || cellValueToJsDate > greaterThanFilterValue) && (!lessThanFilterValue || cellValueToJsDate < lessThanFilterValue);
				},
			},
		});

		const providerValue = {
			headerGroups: table.getHeaderGroups(),
			rows: table.getRowModel().rows,
			columns: table.getAllLeafColumns(),
			pagination,
			parsedApiFilters,
			queryParams,
			setPagination,
			paginationServerState,
			columnFilters,
			isServerSide,
			search,
			table,
			isSelectable,
			selectedRows: rowSelection,
			selectedItemsActions,
			isFetching: dataQuery.isFetching || isLoading,
			isPending: dataQuery.isPending,
			error: dataQuery.error,
			refetchData: dataQuery.refetch,
			onClickRow: onClickRow,
			emptyDataText: emptyDataText,
			emptyDataIcon: emptyDataIcon,
			customQueryKey,
			selectAllFn: selectAllFn,
		};

		return <DataCollectionContext.Provider value={providerValue}>{children}</DataCollectionContext.Provider>;
	};

	const useDataCollection = () => {
		const context = useContext(DataCollectionContext);
		if (!context) {
			throw new Error('useDataCollection must be used within a DataCollectionContextProvider');
		}
		return context;
	};

	const { DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = createDataCollectionTable(useDataCollection);

	return { useDataCollection, DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader };
}
